<template>
    <card-list>
        <subheader-list-item icon="fa-align-left" title="Course Information" />

        <container-list-item padded no-border>
            <b-field label="Title" label-position="inside">
                <b-input v-model="course.title"></b-input>
            </b-field>

            <b-field label="Code" label-position="inside">
                <b-input v-model="course.code" v-mask="'NNNNNNNNNNNNNNN'" maxlength="20"></b-input>
            </b-field>

        </container-list-item>

        <!-- Course Categories -->
        <template v-if="course.id">
            <subheader-list-item icon="fa-folder-tree" title="Course Categories" />

            <card-list-item v-for="cat in courseCategories" :key="cat.id">
                <template #icon><i class="fa-fw fas fa-folder"></i></template>
                <template #content>
                    {{ cat.certification_title }} &rarr; {{ cat.title }}
                </template>
                <template #details>
                    <div class="mt-3">
                        <toolbar class="toolbar">
                            <button class="icon-only" @click.stop="removeCategory(cat)"><i class="far fa-trash"></i></button>
                        </toolbar>
                    </div>
                </template>
            </card-list-item>

            <card-list-item class="pb-5" no-hover>
                <template #icon><i class="fa-fw fas fa-plus"></i></template>
                <template #content>
                    <div class="columns">
                        <div class="column is-10">
                            <b-select expanded placeholder="Category" v-model="categorySelect">
                                <option :value="c" v-for="c in availableCategories" :key="c.id">{{ c.certification_title }} &rarr; {{ c.title }}</option>
                            </b-select>
                        </div>
                        <div class="column has-text-right">
                            <p class="control">
                                <button class="button is-primary" :disabled="!categorySelect" @click="addCategory"><i class="far fa-fw fa-plus"></i></button>
                            </p>
                        </div>
                    </div>
                </template>
            </card-list-item>
        </template>

        <subheader-list-item icon="fa-align-left" title="Course details" />

        <container-list-item padded no-border>
            <b-field label="Description" label-position="inside">
                <b-input type="textarea" v-model="course.description"></b-input>
            </b-field>
            <b-field label="Welcome Message">
                <quill-editor v-model="course.welcome" ref="descriptionEditor" :options="editorOptions" class="has-background-white content" />
            </b-field>
        </container-list-item>

        <!-- Self-Directed Course Configuration -->
        <subheader-list-item icon="fa-compass" title="Self-Directed Course Setup" />
        <container-list-item padded no-border>
            <b-field>
                <b-switch v-model="isSelfDirected">Enable Self-Directed Course Mode</b-switch>
            </b-field>
            <p class="is-size-7 has-text-grey mb-4">
                Self-directed courses require users to complete a setup wizard where they select goals, objectives, and allocate hours before accessing course content.
            </p>
        </container-list-item>

        <template v-if="isSelfDirected">
            <!-- Custom Course Naming -->
            <subheader-list-item icon="fa-tag" title="Custom Course Naming" subtitle="Allow users to customize their course name" />
            <container-list-item padded no-border>
                <b-field>
                    <b-switch v-model="hasCustomNaming">Enable Custom Course Naming</b-switch>
                </b-field>
                <p class="is-size-7 has-text-grey mb-4">
                    Users can build a personalized course name by selecting from options you define below.
                </p>

                <template v-if="hasCustomNaming">
                    <b-field label="Course Name Prefix" label-position="inside">
                        <b-input v-model="selfDirectedConfig.custom_naming.prefix" placeholder="e.g., Developing Rigorous and Relevant Curriculum:"></b-input>
                    </b-field>
                    <p class="is-size-7 has-text-grey mb-4">
                        Preview: <strong>{{ courseNamePreview }}</strong>
                    </p>

                    <b-field>
                        <b-switch v-model="selfDirectedConfig.custom_naming.allow_custom_entry">Allow Custom Entry</b-switch>
                    </b-field>
                    <p class="is-size-7 has-text-grey mb-4">
                        Users can type their own text after the prefix instead of selecting from options.
                    </p>

                    <div class="columns">
                        <div class="column">
                            <b-field>
                                <b-switch v-model="hasCustom1Options">First Option Set</b-switch>
                            </b-field>
                            <template v-if="hasCustom1Options">
                                <label class="label is-size-7">Options (one per line)</label>
                                <b-field>
                                    <b-input type="textarea" v-model="custom1OptionsText" placeholder="Enter each option on a new line"></b-input>
                                </b-field>
                            </template>
                        </div>
                        <div class="column">
                            <b-field>
                                <b-switch v-model="hasCustom2Options">Second Option Set</b-switch>
                            </b-field>
                            <template v-if="hasCustom2Options">
                                <label class="label is-size-7">Options (one per line)</label>
                                <b-field>
                                    <b-input type="textarea" v-model="custom2OptionsText" placeholder="Enter each option on a new line"></b-input>
                                </b-field>
                            </template>
                        </div>
                    </div>
                </template>
            </container-list-item>

            <!-- Goals -->
            <subheader-list-item icon="fa-bullseye" title="Goals" subtitle="Users select from these goals during setup" />
            <container-list-item padded no-border>
                <div class="mb-3" v-for="(goal, index) in selfDirectedConfig.goals" :key="'goal-' + index">
                    <b-field>
                        <b-input v-model="goal.text" placeholder="Goal text..." expanded></b-input>
                        <p class="control">
                            <b-button type="is-danger" outlined @click="removeGoal(index)">
                                <i class="fas fa-times"></i>
                            </b-button>
                        </p>
                    </b-field>
                </div>
                <b-button type="is-primary" size="is-small" outlined @click="addGoal">
                    <i class="fas fa-plus mr-2"></i>Add Goal
                </b-button>
            </container-list-item>

            <!-- Objectives -->
            <subheader-list-item icon="fa-list-check" title="Objectives" subtitle="Users select objectives and allocate hours" />
            <container-list-item padded no-border>
                <div class="mb-4" v-for="(obj, index) in selfDirectedConfig.objectives" :key="'obj-' + index">
                    <b-field>
                        <b-input v-model="obj.text" placeholder="Objective text..." expanded></b-input>
                        <p class="control">
                            <b-button type="is-danger" outlined @click="removeObjective(index)">
                                <i class="fas fa-times"></i>
                            </b-button>
                        </p>
                    </b-field>
                    <div class="columns is-mobile ml-4">
                        <div class="column is-narrow">
                            <b-field label="Min Hours" label-position="inside">
                                <b-input v-model.number="obj.min_hours" type="number" min="0" step="0.5" style="width: 100px;"></b-input>
                            </b-field>
                        </div>
                        <div class="column is-narrow">
                            <b-field label="Suggested Hours" label-position="inside">
                                <b-input v-model.number="obj.suggested_hours" type="number" min="0" step="0.5" style="width: 120px;"></b-input>
                            </b-field>
                        </div>
                    </div>
                </div>
                <b-button type="is-primary" size="is-small" outlined @click="addObjective">
                    <i class="fas fa-plus mr-2"></i>Add Objective
                </b-button>
            </container-list-item>

            <!-- Materials -->
            <subheader-list-item icon="fa-book" title="Materials" subtitle="Materials users may need for the course" />
            <container-list-item padded no-border>
                <div class="mb-3" v-for="(material, index) in selfDirectedConfig.materials" :key="'mat-' + index">
                    <b-field>
                        <b-input v-model="selfDirectedConfig.materials[index]" placeholder="Material..." expanded></b-input>
                        <p class="control">
                            <b-button type="is-danger" outlined @click="removeMaterial(index)">
                                <i class="fas fa-times"></i>
                            </b-button>
                        </p>
                    </b-field>
                </div>
                <b-button type="is-primary" size="is-small" outlined @click="addMaterial">
                    <i class="fas fa-plus mr-2"></i>Add Material
                </b-button>
            </container-list-item>

            <!-- Resources -->
            <subheader-list-item icon="fa-link" title="Resources" subtitle="Helpful links and resources" />
            <container-list-item padded no-border>
                <div class="mb-3" v-for="(resource, index) in selfDirectedConfig.resources" :key="'res-' + index">
                    <b-field>
                        <b-input v-model="resource.title" placeholder="Resource title..." expanded></b-input>
                        <p class="control">
                            <b-button type="is-danger" outlined @click="removeResource(index)">
                                <i class="fas fa-times"></i>
                            </b-button>
                        </p>
                    </b-field>
                    <b-field class="ml-4">
                        <b-input v-model="resource.url" placeholder="URL (optional)" expanded></b-input>
                    </b-field>
                </div>
                <b-button type="is-primary" size="is-small" outlined @click="addResource">
                    <i class="fas fa-plus mr-2"></i>Add Resource
                </b-button>
            </container-list-item>

            <!-- Settings -->
            <subheader-list-item icon="fa-cog" title="Setup Settings" />
            <container-list-item padded no-border>
                <div class="columns">
                    <div class="column is-half">
                        <b-field>
                            <b-switch v-model="selfDirectedConfig.settings.allow_custom_goals">Allow Custom Goals</b-switch>
                        </b-field>
                        <b-field>
                            <b-switch v-model="selfDirectedConfig.settings.allow_custom_objectives">Allow Custom Objectives</b-switch>
                        </b-field>
                    </div>
                    <div class="column is-half">
                        <b-field label="Min Goals Required" label-position="inside">
                            <b-input v-model.number="selfDirectedConfig.settings.min_goals" type="number" min="1"></b-input>
                        </b-field>
                        <b-field label="Min Objectives Required" label-position="inside">
                            <b-input v-model.number="selfDirectedConfig.settings.min_objectives" type="number" min="1"></b-input>
                        </b-field>
                    </div>
                </div>
            </container-list-item>
        </template>

        <subheader-list-item icon="fa-align-left" title="Advanced Options" />
        <container-list-item padded no-border>
            <div class="columns">
                <div class="column">
                    <b-field label="Logo URL" label-position="inside">
                        <b-input v-model="course.logo"></b-input>
                    </b-field>
                    <b-field label="Logo (White) URL" label-position="inside">
                        <b-input v-model="course.logo_white"></b-input>
                    </b-field>
                    <b-field class="mt-5">
                        <b-switch v-model="course.active" :true-value="1" :false-value="0">Course Active</b-switch>
                    </b-field>
                    <b-field>
                        <b-switch v-model="course.is_hidden" :true-value="1" :false-value="0">Hidden from Course Catalog (Code Only)</b-switch>
                    </b-field>
                    <hr />
<!--                    <b-field>-->
<!--                        <b-switch v-model="course.show_welcome" :true-value="1" :false-value="0">Show Course Welcome Screen</b-switch>-->
<!--                    </b-field>-->
                    <b-field>
                        <b-switch v-model="course.show_titles" :true-value="1" :false-value="0">Show Lesson Header Titles</b-switch>
                    </b-field>
                    <b-field>
                        <b-switch v-model="course.content_framed" :true-value="1" :false-value="0">Lesson Content is Framed</b-switch>
                    </b-field>
                    <b-field>
                        <b-switch v-model="course.show_timer" :true-value="1" :false-value="0">Show Lesson Timer</b-switch>
                    </b-field>
                    <hr />
                    <b-field>
                        <b-switch v-model="course.test_mode" :true-value="1" :false-value="0">Test Mode</b-switch>
                    </b-field>
                    <b-field>
                        <b-switch v-model="course.disable_timer" :true-value="1" :false-value="0">Ignore Required Time</b-switch>
                    </b-field>
                </div>
            </div>

        </container-list-item>

    </card-list>
</template>

<script>
    import CardList from "@/TIER/components/CardList";
    import SubheaderListItem from "@/TIER/components/SubheaderListItem";
    import ContainerListItem from "@/TIER/components/ContainerListItem";
    import CardListItem from "@/TIER/components/CardListItem";
    import SeparatorListItem from "@/components/common/SeparatorListItem";
    import NavigationListItem from "@/TIER/components/NavigationListItem";
    import {client as http} from "@/http_client";
    import draggable from "vuedraggable";
    import Toolbar from "@/TIER/components/Toolbar";

    export default {
        name: 'CourseForm',
        props: {
            course: {
                type: Object,
                required: true
            },
            card: {
                type: Object,
                required: true
            },
            child: {
                type: Object,
                required: false
            }
        },
        components: {SeparatorListItem, CardListItem, ContainerListItem, SubheaderListItem, CardList, NavigationListItem, draggable, Toolbar},
        data: function () {
            return {
                type: null,
                title: null,
                preComponentSelect: null,
                postComponentSelect: null,
                sectionSelect: null,
                components: [],
                sections: [],
                editorOptions: {},
                // Category management
                allCategories: [],
                courseCategories: [],
                categorySelect: null,
                // Default self-directed config structure
                defaultSelfDirectedConfig: {
                    goals: [],
                    objectives: [],
                    materials: [],
                    resources: [],
                    custom_naming: null,
                    settings: {
                        allow_custom_goals: true,
                        allow_custom_objectives: true,
                        min_goals: 2,
                        min_objectives: 2
                    }
                },
                // Default custom naming structure
                defaultCustomNaming: {
                    prefix: ''
                }
            };
        },
        computed: {
            isSelfDirected: {
                get() {
                    return this.course.type === 'self_directed';
                },
                set(value) {
                    this.course.type = value ? 'self_directed' : 'guided';
                    // Initialize config if enabling self-directed
                    if (value) {
                        this.ensureConfig();
                    }
                }
            },
            selfDirectedConfig() {
                // Return the actual object for reactivity, ensuring it exists
                if (!this.course.self_directed_config) {
                    return this.defaultSelfDirectedConfig;
                }
                return this.course.self_directed_config;
            },
            hasCustomNaming: {
                get() {
                    return this.selfDirectedConfig.custom_naming != null;
                },
                set(value) {
                    this.ensureConfig();
                    if (value) {
                        this.$set(this.course.self_directed_config, 'custom_naming', JSON.parse(JSON.stringify(this.defaultCustomNaming)));
                    } else {
                        this.$set(this.course.self_directed_config, 'custom_naming', null);
                    }
                }
            },
            hasCustom1Options: {
                get() {
                    return this.selfDirectedConfig.custom_naming?.custom1_options != null;
                },
                set(value) {
                    if (this.selfDirectedConfig.custom_naming) {
                        if (value) {
                            this.$set(this.selfDirectedConfig.custom_naming, 'custom1_options', []);
                        } else {
                            this.$delete(this.selfDirectedConfig.custom_naming, 'custom1_options');
                        }
                    }
                }
            },
            hasCustom2Options: {
                get() {
                    return this.selfDirectedConfig.custom_naming?.custom2_options != null;
                },
                set(value) {
                    if (this.selfDirectedConfig.custom_naming) {
                        if (value) {
                            this.$set(this.selfDirectedConfig.custom_naming, 'custom2_options', []);
                        } else {
                            this.$delete(this.selfDirectedConfig.custom_naming, 'custom2_options');
                        }
                    }
                }
            },
            custom1OptionsText: {
                get() {
                    if (!this.selfDirectedConfig.custom_naming?.custom1_options) {
                        return '';
                    }
                    return this.selfDirectedConfig.custom_naming.custom1_options.join('\n');
                },
                set(value) {
                    if (this.selfDirectedConfig.custom_naming) {
                        this.selfDirectedConfig.custom_naming.custom1_options = value.split('\n').map(s => s.trim()).filter(s => s);
                    }
                }
            },
            custom2OptionsText: {
                get() {
                    if (!this.selfDirectedConfig.custom_naming?.custom2_options) {
                        return '';
                    }
                    return this.selfDirectedConfig.custom_naming.custom2_options.join('\n');
                },
                set(value) {
                    if (this.selfDirectedConfig.custom_naming) {
                        this.selfDirectedConfig.custom_naming.custom2_options = value.split('\n').map(s => s.trim()).filter(s => s);
                    }
                }
            },
            courseNamePreview() {
                if (!this.selfDirectedConfig.custom_naming) return '';
                const prefix = this.selfDirectedConfig.custom_naming.prefix || '[Prefix]';
                const parts = [prefix];
                if (this.selfDirectedConfig.custom_naming.custom1_options) {
                    parts.push(this.selfDirectedConfig.custom_naming.custom1_options[0] || '[Option 1]');
                }
                if (this.selfDirectedConfig.custom_naming.custom2_options) {
                    parts.push(this.selfDirectedConfig.custom_naming.custom2_options[0] || '[Option 2]');
                }
                return parts.join(' ');
            },
            availableCategories() {
                const assignedIds = this.courseCategories.map(c => c.id);
                return this.allCategories.filter(c => !assignedIds.includes(c.id));
            }
        },
        watch: {
            'course.id': {
                immediate: true,
                handler() {
                    // When course loads, ensure config structure is complete
                    if (this.course.type === 'self_directed') {
                        this.ensureConfig();
                    }
                    // Load course categories when course id changes
                    if (this.course.id) {
                        this.loadCourseCategories();
                    }
                }
            }
        },
        methods: {
            loadSections(force) {
                this.$emit('loading', true);

                // http.get('components/', {force}).then(response => {
                //     this.components = response.data;
                //     this.$emit('loading', false);
                // }, err => {
                //     console.log(err);
                // });

                http.get('sections/', {force}).then(response => {
                    this.sections = response.data;
                    this.$emit('loading', false);
                }, err => {
                    console.log(err);
                });
            },
            addOutline() {
                http.post('courses/outline/', {
                    type: 'SECTION',
                    course_id: this.course.id,
                    type_id: (this.sectionSelect) ? this.sectionSelect.id : null,
                    sort: this.course.course_outline.length + 1,
                    title: this.title
                }, {force: true}).then(response => {
                    console.log(response.data);
                    this.course.course_outline.push(response.data);
                    this.type = null;
                    this.title = '';
                    this.sectionSelect = null;
                }, err => {
                    console.log(err);
                });
            },
            addRequiredComponent(type, component) {
                http.post('courses/component/', {
                    type: type,
                    entity_id: this.course.id,
                    component_id: component.id
                }, {force: true}).then(response => {
                    if (type === 'pre') {
                        this.preComponentSelect = null;
                    } else {
                        this.postComponentSelect = null;
                    }
                }, err => {
                    console.log(err);
                });
            },
            completeDrag: function () {
                http.post('courses/outline/order', this.course.course_outline.map(i => i.id)).then(response => {

                });
            },
            // Self-directed config methods
            ensureConfig() {
                if (!this.course.self_directed_config) {
                    this.$set(this.course, 'self_directed_config', JSON.parse(JSON.stringify(this.defaultSelfDirectedConfig)));
                } else {
                    // Ensure all nested properties exist
                    if (!this.course.self_directed_config.goals) {
                        this.$set(this.course.self_directed_config, 'goals', []);
                    }
                    if (!this.course.self_directed_config.objectives) {
                        this.$set(this.course.self_directed_config, 'objectives', []);
                    }
                    if (!this.course.self_directed_config.materials) {
                        this.$set(this.course.self_directed_config, 'materials', []);
                    }
                    if (!this.course.self_directed_config.resources) {
                        this.$set(this.course.self_directed_config, 'resources', []);
                    }
                    if (!this.course.self_directed_config.settings) {
                        this.$set(this.course.self_directed_config, 'settings', {
                            allow_custom_goals: true,
                            allow_custom_objectives: true,
                            min_goals: 2,
                            min_objectives: 2
                        });
                    }
                    // custom_naming options are optional - no need to ensure they exist
                }
            },
            addGoal() {
                this.ensureConfig();
                const id = 'goal_' + (this.course.self_directed_config.goals.length + 1) + '_' + Date.now();
                this.course.self_directed_config.goals.push({
                    id: id,
                    text: ''
                });
            },
            removeGoal(index) {
                this.course.self_directed_config.goals.splice(index, 1);
            },
            addObjective() {
                this.ensureConfig();
                const id = 'obj_' + (this.course.self_directed_config.objectives.length + 1) + '_' + Date.now();
                this.course.self_directed_config.objectives.push({
                    id: id,
                    text: '',
                    min_hours: 0,
                    suggested_hours: null
                });
            },
            removeObjective(index) {
                this.course.self_directed_config.objectives.splice(index, 1);
            },
            addMaterial() {
                this.ensureConfig();
                this.course.self_directed_config.materials.push('');
            },
            removeMaterial(index) {
                this.course.self_directed_config.materials.splice(index, 1);
            },
            addResource() {
                this.ensureConfig();
                this.course.self_directed_config.resources.push({
                    title: '',
                    url: ''
                });
            },
            removeResource(index) {
                this.course.self_directed_config.resources.splice(index, 1);
            },
            // Category management methods
            loadAllCategories() {
                http.get('certs').then(response => {
                    const certs = response.data;
                    const promises = certs.map(cert =>
                        http.get('course-categories/', {
                            params: { certificationId: cert.id }
                        }).then(res => ({ cert, categories: res.data }))
                    );
                    Promise.all(promises).then(results => {
                        const flat = [];
                        results.forEach(({ cert, categories }) => {
                            categories.forEach(cat => {
                                flat.push({
                                    id: cat.id,
                                    title: cat.title,
                                    certification_title: cert.title
                                });
                            });
                        });
                        flat.sort((a, b) => {
                            const certCmp = a.certification_title.localeCompare(b.certification_title);
                            if (certCmp !== 0) return certCmp;
                            return a.title.localeCompare(b.title);
                        });
                        this.allCategories = flat;
                    });
                }).catch(err => {
                    console.log('Error loading categories:', err);
                });
            },
            loadCourseCategories() {
                if (!this.course.id) return;
                http.get(`courses/${this.course.id}/categories`).then(response => {
                    // Enrich with certification_title from allCategories
                    this.courseCategories = response.data.map(cat => {
                        const full = this.allCategories.find(c => c.id === cat.id);
                        return {
                            ...cat,
                            certification_title: full ? full.certification_title : ''
                        };
                    });
                }).catch(err => {
                    console.log('Error loading course categories:', err);
                });
            },
            addCategory() {
                if (!this.categorySelect) return;
                this.courseCategories.push(this.categorySelect);
                this.categorySelect = null;
                this.saveCourseCategories();
            },
            removeCategory(cat) {
                this.courseCategories.splice(this.courseCategories.indexOf(cat), 1);
                this.saveCourseCategories();
            },
            saveCourseCategories() {
                if (!this.course.id) return;
                const category_ids = this.courseCategories.map(c => c.id);
                http.put(`courses/${this.course.id}/categories`, { category_ids }).catch(err => {
                    console.log('Error saving course categories:', err);
                });
            }
        },
        mounted: function () {
            this.loadSections();
            this.loadAllCategories();
            if (this.course.id) {
                this.loadCourseCategories();
            }
        }
    };
</script>

<style lang="scss" scoped>
    .drag-handle {
        cursor: ns-resize;
    }
</style>
